import { useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    eventExtended,
    familyExtended,
    fetchAncestorFamilies,
    fetchAncestorPersons,
    fetchEventsByHandles,
    mapRestMedia,
    personExtended,
    restPlaceName,
    type RestEvent,
    type RestFamily,
    type RestNote,
    type RestPerson,
} from '../api/grampsRest';
import { readBearerToken, subscribeToBearerToken } from '../grampsAuth';
import type { GrampsEventMedia } from '../utils';

export { restPersonName } from '../api/grampsRest';

export interface NotableNoteEntry {
    key: string;
    notes: RestNote[];
    person: RestPerson;
    event?: RestEvent;
    year: number | undefined;
    placeText: string | undefined;
    media: GrampsEventMedia[];
}

function personBirthYear(person: RestPerson): number | undefined {
    const birthEvent = (personExtended(person).events ?? []).find((e) => e.type === 'Birth');
    return birthEvent?.date?.year;
}

interface NotedEventRef {
    handle: string;
    person: RestPerson;
}

export function useAncestorPersons(personGrampsId: string | null, treeName: string | null) {
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    return useQuery({
        queryKey: ['ancestors', 'people', treeName, personGrampsId],
        queryFn: () => fetchAncestorPersons(personGrampsId!, token),
        enabled: !!personGrampsId,
    });
}

export function useAncestorFamilies(personGrampsId: string | null, treeName: string | null) {
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    return useQuery({
        queryKey: ['ancestors', 'families', treeName, personGrampsId],
        queryFn: () => fetchAncestorFamilies(personGrampsId!, token),
        enabled: !!personGrampsId,
    });
}

function collectNotedEventRefs(people: RestPerson[], families: RestFamily[]): NotedEventRef[] {
    const peopleByHandle = new Map(people.filter((p) => p.handle).map((p) => [p.handle!, p]));
    const refs: NotedEventRef[] = [];
    const seen = new Set<string>();

    for (const person of people) {
        for (const event of personExtended(person).events ?? []) {
            if (event.handle && (event.note_list?.length ?? 0) > 0 && !seen.has(event.handle)) {
                seen.add(event.handle);
                refs.push({ handle: event.handle, person });
            }
        }
    }

    for (const family of families) {
        // Tag with the ancestor who is a parent in this family (their own marriage) first,
        // matching the pre-migration semantics (getParentFamilies(person) -> getMarriages).
        // Fall back to a child ancestor only for families reached solely via a non-birth
        // childof relation (e.g. adoption), where neither parent is on the direct line.
        const parentAncestor = [family.father_handle, family.mother_handle]
            .map((h) => (h ? peopleByHandle.get(h) : undefined))
            .find((p): p is RestPerson => !!p);
        const childAncestor = (family.child_ref_list ?? [])
            .map((ref) => (ref.ref ? peopleByHandle.get(ref.ref) : undefined))
            .find((p): p is RestPerson => !!p);
        const taggedPerson = parentAncestor ?? childAncestor;
        if (!taggedPerson) {
            continue;
        }

        for (const event of familyExtended(family).events ?? []) {
            if (event.handle && (event.note_list?.length ?? 0) > 0 && !seen.has(event.handle)) {
                seen.add(event.handle);
                refs.push({ handle: event.handle, person: taggedPerson });
            }
        }
    }

    return refs;
}

export function useNotableNotes(mainId: string | null, treeName: string | null) {
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    const peopleQuery = useAncestorPersons(mainId, treeName);
    const familiesQuery = useAncestorFamilies(mainId, treeName);

    const people = useMemo(() => peopleQuery.data ?? [], [peopleQuery.data]);
    const families = useMemo(() => familiesQuery.data ?? [], [familiesQuery.data]);

    const notedEventRefs = useMemo(() => collectNotedEventRefs(people, families), [people, families]);
    const notedEventHandles = useMemo(
        () => [...new Set(notedEventRefs.map((ref) => ref.handle))].sort(),
        [notedEventRefs],
    );

    const notedEventsQuery = useQuery({
        queryKey: ['events', treeName, 'batch', notedEventHandles],
        queryFn: () => fetchEventsByHandles(notedEventHandles, token),
        enabled: notedEventHandles.length > 0,
    });

    const notedEventsByHandle = useMemo(() => {
        const map = new Map<string, RestEvent>();
        for (const event of notedEventsQuery.data ?? []) {
            if (event.handle) {
                map.set(event.handle, event);
            }
        }
        return map;
    }, [notedEventsQuery.data]);

    const notes = useMemo<NotableNoteEntry[]>(() => {
        const result: NotableNoteEntry[] = [];

        for (const person of people) {
            const personNotes = personExtended(person).notes ?? [];
            if (personNotes.length === 0) {
                continue;
            }
            result.push({
                key: `person-${person.handle}`,
                notes: personNotes,
                person,
                event: undefined,
                year: personBirthYear(person),
                placeText: undefined,
                media: mapRestMedia(personExtended(person).media),
            });
        }

        notedEventRefs.forEach((ref) => {
            const event = notedEventsByHandle.get(ref.handle);
            if (!event) {
                return;
            }
            const ext = eventExtended(event);
            const eventNotes = ext.notes ?? [];
            if (eventNotes.length === 0) {
                return;
            }
            result.push({
                key: `event-${event.handle}`,
                notes: eventNotes,
                person: ref.person,
                event,
                year: event.date?.year,
                placeText: restPlaceName(ext.place),
                media: mapRestMedia(ext.media),
            });
        });

        result.sort((a, b) => {
            const aYear = a.year ?? Number.NEGATIVE_INFINITY;
            const bYear = b.year ?? Number.NEGATIVE_INFINITY;
            if (aYear === bYear) {
                const aChange = Math.max(0, ...a.notes.map((n) => n.change ?? 0));
                const bChange = Math.max(0, ...b.notes.map((n) => n.change ?? 0));
                return bChange - aChange;
            }
            return bYear - aYear;
        });

        return result;
    }, [people, notedEventRefs, notedEventsByHandle]);

    const isLoading = peopleQuery.isLoading || familiesQuery.isLoading || notedEventsQuery.isLoading;

    return { notes, isLoading };
}
